/**
 * Zod v4 schemas — exact mirrors of Post For Me SDK types.
 *
 * Every schema validates the RAW backend response (snake_case, SDK field names).
 * Transforms in api.ts consume the parsed output and map to frontend camelCase types.
 *
 * Source of truth: node_modules/post-for-me/resources/*.d.ts
 */

import { z } from "zod";

// ============================================
// LITERALS (shared across schemas)
// ============================================

const platformLiteral = z.string();

const postStatusLiteral = z.union([
  z.literal("draft"),
  z.literal("scheduled"),
  z.literal("processing"),
  z.literal("processed"),
]);

const socialAccountStatusLiteral = z.union([z.literal("connected"), z.literal("disconnected")]);

const tagPlatformLiteral = z.union([z.literal("facebook"), z.literal("instagram")]);

const tagTypeLiteral = z.union([z.literal("user"), z.literal("product")]);

// ============================================
// NESTED TYPES
// ============================================

/** Media.Tag — SDK: { id: string; platform: 'facebook' | 'instagram'; type: 'user' | 'product'; x?: number; y?: number } */
export const sdkMediaTagSchema = z.object({
  id: z.string(),
  platform: tagPlatformLiteral,
  type: tagTypeLiteral,
  x: z.number().optional(),
  y: z.number().optional(),
});

/** SocialPost.Media — SDK media item shape */
export const sdkMediaItemSchema = z.object({
  url: z.string(),
  skip_processing: z.boolean().nullable().optional(),
  tags: z.array(sdkMediaTagSchema).nullable().optional(),
  thumbnail_timestamp_ms: z.unknown().nullable().optional(),
  thumbnail_url: z.unknown().nullable().optional(),
});

/** SocialPost.AccountConfiguration — SDK: { social_account_id: string; configuration: Record<string, unknown> } */
export const sdkAccountConfigurationSchema = z.object({
  social_account_id: z.string(),
  configuration: z.record(z.string(), z.unknown()),
});

/** SocialPostResult.PlatformData — SDK: { id?: string; url?: string } */
export const sdkPlatformDataSchema = z
  .object({
    id: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
  })
  .nullable()
  .optional();

// ============================================
// CORE ENTITIES (raw backend shape, snake_case)
// ============================================

/** SDK SocialAccount — exact field set from social-accounts.d.ts */
export const sdkSocialAccountSchema = z.object({
  id: z.string(),
  access_token: z.string().optional(),
  access_token_expires_at: z.string().optional(),
  external_id: z.string().nullable(),
  metadata: z.unknown().nullable(),
  platform: platformLiteral,
  profile_photo_url: z.string().nullable(),
  refresh_token: z.string().nullable().optional(),
  refresh_token_expires_at: z.string().nullable().optional(),
  status: socialAccountStatusLiteral,
  user_id: z.string(),
  username: z.string().nullable(),
});

/** SDK SocialPost — exact field set from social-posts.d.ts */
export const sdkSocialPostSchema = z.object({
  id: z.string(),
  account_configurations: z.array(sdkAccountConfigurationSchema).nullable(),
  caption: z.string(),
  created_at: z.string(),
  external_id: z.string().nullable(),
  media: z.array(sdkMediaItemSchema).nullable(),
  platform_configurations: z.record(z.string(), z.unknown()).nullable(),
  scheduled_at: z.string().nullable(),
  social_accounts: z.array(sdkSocialAccountSchema),
  status: postStatusLiteral,
  updated_at: z.string(),
});

/** SDK SocialPostResult — exact field set from social-post-results.d.ts */
export const sdkSocialPostResultSchema = z.object({
  id: z.string(),
  details: z.unknown(),
  error: z.unknown().nullable().optional(),
  platform_data: sdkPlatformDataSchema,
  post_id: z.string(),
  social_account_id: z.string(),
  success: z.boolean(),
  created_at: z.string().optional(),
});

/** SDK PlatformPost — exact field set from social-account-feeds.d.ts */
export const sdkPlatformPostSchema = z.object({
  caption: z.string(),
  media: z.array(z.unknown()).nullable(),
  platform: platformLiteral,
  platform_account_id: z.string(),
  platform_post_id: z.string(),
  platform_url: z.string(),
  social_account_id: z.string(),
  external_account_id: z.string().nullable().optional(),
  external_post_id: z.string().nullable().optional(),
  metrics: z.unknown().optional(),
  posted_at: z.string().optional(),
  social_post_id: z.string().nullable().optional(),
  social_post_result_id: z.string().nullable().optional(),
});

/** Webhook — aligned with PostForMe WebhookDto */
export const sdkWebhookSchema = z.object({
  id: z.string(),
  url: z.string(),
  event_types: z.array(z.string()),
  secret: z.string(),
});

// ============================================
// RESPONSE WRAPPERS
// ============================================

/** Worker list response: { items?: T[]; data?: T[]; total?: number; count?: number } */
export function sdkListResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema).optional(),
    data: z.array(itemSchema).optional(),
    total: z.number().optional(),
    count: z.number().optional(),
  });
}

/** Cursor-paginated response: { data: T[]; meta: { cursor: string; limit: number; next: string | null; has_more: boolean } } */
export function sdkCursorResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    meta: z.object({
      cursor: z.string(),
      limit: z.number(),
      next: z.string().nullable(),
      has_more: z.boolean(),
    }),
  });
}

/** Auth URL response: { url: string } */
export const sdkAuthUrlResponseSchema = z.object({
  url: z.string(),
});

/** Simple success response: { success: boolean } */
export const sdkSuccessResponseSchema = z.object({
  success: z.boolean(),
});

// ============================================
// INFERRED TYPES (for use in transforms)
// ============================================

export type SDKSocialAccount = z.infer<typeof sdkSocialAccountSchema>;
export type SDKSocialPost = z.infer<typeof sdkSocialPostSchema>;
export type SDKSocialPostResult = z.infer<typeof sdkSocialPostResultSchema>;
export type SDKPlatformPost = z.infer<typeof sdkPlatformPostSchema>;
export type SDKWebhook = z.infer<typeof sdkWebhookSchema>;
export type SDKMediaItem = z.infer<typeof sdkMediaItemSchema>;
export type SDKMediaTag = z.infer<typeof sdkMediaTagSchema>;
export type SDKAccountConfiguration = z.infer<typeof sdkAccountConfigurationSchema>;
export type SDKPlatformData = z.infer<typeof sdkPlatformDataSchema>;
